import {
	ActionMenuPopover,
	ActionsMenuOption,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	deleteAction,
	FormControlLabel,
	FormLabel,
	IconButton,
	IconButtonProps,
	MuiRadio,
	MuiRadioGroup,
	Select,
	TextField,
	TreeSelect,
	TreeSelectItemProps,
	TreeSelectOption,
	tryParseJSONObject,
	Typography
} from '@fox/atoms';
import { FlexColumnSpaced, FlexRow, FlexRowSpacedCentered } from '@fox/layout';
import { Extension, getExtension, getExtensions } from 'fox-extensions';
import { Draft, freeze, produce } from 'immer';
import React, { ComponentType, Dispatch, useCallback, useContext, useMemo, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { FilterOperatorProps } from '../contracts/FilterOperatorProps';
import { FilterExtensionProps } from '../contracts/filters';
import { DataSourceExtension } from './types';

export type StandardFilterTypes = 'string' | 'number' | 'datetime' | 'date' | 'select' | 'single-select' | 'link' | 'boolean' | 'ownermodel' | 'currency-minor';

export type FilterComponentProps<T = any, TProps = any> = {
	filter: T;
	onUpdate: Dispatch<T>;
	props: TProps;
};

export type FilterExtension<TProps = {}> = {
	field: string;
	nested?: string;
	label: string;
	category: string;
	FilterComponent?: ComponentType<FilterComponentProps<TProps>>;
	filterComponentProps?: TProps;
	/**
	 * If specified, called as part of assembling the final criteria for submission to the flex compiler
	 */
	encodeValue?: (value: string | string[]| null) => string | string[] | null;
	/**
	 * If specified, called as part of loading the criteria into the list builder UI
	 */
	decodeValue?: (value: string | string[] | null) => string | string[] | null;
	type: StandardFilterTypes;
	filterParams?: unknown;
	elasticPath?: string;
	dataSource?: DataSourceExtension;
};

export type SelectFilterExtension<T = unknown> = (args: T, searchString?: string, selectedValueIds?: string[]) => Promise<{ id: string; name: string; group?: string }[]>;

/** This represents a single row filter */
export type CriteriaItem = {
	/** The type of the filter, this would refer to the path of the property, eg name, fee interval or */
	field: string | null;

	/** The type of the filter */
	type: string | null;

	/** For nested documents, this would refer to the property that it is nested under, for example subscription, or membership (as in org relationship) */
	nested?: string;

	/** The modifier for the selection, could be a contains, or is, or gt, or lt, - it would depend on the type */
	modifier: string | null;

	/** The value for a filter to decide what to do - it could be a string for text search or a date in ISO format, perhaps a magic date property @today, or perhaps an array representing the IDs of the organisations we're filtering against */
	value: string | null;

	values: string[] | null;
};

const EMPTY_FILTER = { field: null, type: null, modifier: null, value: null, values: null };
const EMPTY_GROUP = { items: [EMPTY_FILTER], filtersOp: 'and' } as const;
const EMPTY_ROOT = { items: [EMPTY_GROUP], groupOp: 'or' } as const;

/** this represents a grouping of filters, which could nest other filters */
export type CriteriaGroup = {
	filtersOp: 'or' | 'and';
	items: readonly Readonly<CriteriaItem>[];
};

export type GroupRoot = {
	items: readonly Readonly<CriteriaGroup>[];
	groupOp: 'or' | 'and';
};

type FilterFieldTreeValue = { field: string; type: string; nested?: string };

enum ChainLogic {
	AND = 'and',
	OR = 'or'
}

interface ExpandMoreProps extends IconButtonProps {
	expand: boolean;
}

const ConditionsGrid = styled.div`
	display: grid;
	grid-template-columns: 30% 20% 30%;
	gap: 1rem;
	width: max(90%, 20rem);
	justify-content: flex-end;
`;

const ExpandMore = styled((props: ExpandMoreProps) => {
	const { expand, ...other } = props;
	return <IconButton {...other} />;
})(({ theme, expand }) => ({
	transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest
	})
}));

export type DraftFunction<S> = (draft: Draft<S>) => void;
export type Updater<S> = (arg: S | DraftFunction<S>) => void;
export type ImmerHook<S> = [S, Updater<S>];

export function useImmerCall<S = any>(initialValue: S | (() => S), onChange?: (val: S) => void): ImmerHook<S> {
	const [val, updateValue] = useState(() => freeze(typeof initialValue === 'function' ? (initialValue as () => S)() : initialValue, true));

	function updateBlah(updater: (prev: S) => S) {
		updateValue(u => {
			const newVal = updater(u);
			onChange?.(newVal);
			return newVal;
		});
	}

	return [
		val,
		useCallback(updater => {
			if (typeof updater === 'function') {
				updateBlah(produce(updater as DraftFunction<S>));
			} else updateBlah(() => freeze(updater));
		}, [])
	];
}

// TODO - An order property would be nice but it's not at FilterExtension level so not sure where it would exist
const FIELD_ORDER = [
	'Profile Details',
	'Custom Fields',
	'Data Table', // placeholder for custom section data table ordering
	'Subscriptions & Financial Data',
	'Organisation Connections',
	'Forms, Surveys, Petitions',
	'Posts & Tags',
	'Events',
	'Stakeholder Leader',
	'Goals'
];

function getDecodedValue(filterField: CriteriaItem, currentOption: FilterExtension | null): string | string[] | null {
	if (currentOption?.decodeValue) {
		return currentOption.decodeValue(Array.isArray(filterField.values) ? filterField.values : filterField.value);
	}
	return Array.isArray(filterField.values) ? filterField.values : filterField.value;
}

function getEncodedValue(value: string | string[] | null, currentOption: FilterExtension): string | string[] | null {
	if (currentOption?.encodeValue) {
		return currentOption.encodeValue(value);
	}
	return value;
}

function FilterField({
	filterField,
	updateFilter,
	options,
	filterType
}: {
	filterField: CriteriaGroup['items'][0];
	updateFilter: (arg: DraftFunction<CriteriaGroup['items'][0]>) => void;
	options: FilterExtension[];
	filterType?: string;
}) {
	const currentOption = options.find(o => o.field === filterField.field && o.nested === filterField.nested && o.type === filterField.type) ?? null;
	const filterOperatorsExt = getExtension<FilterOperatorProps>(`column-operators-${currentOption?.type}`);
	const FilterComp = currentOption?.FilterComponent;
	const filterFieldValue = filterField?.values?.length ? filterField?.values : filterField?.value;
	const contraintOptions = filterOperatorsExt?.filterConstraints?.filter(f => {
		const match = filterOperatorsExt.appliesTo?.[f];
		if (match) {
			return match.includes(filterType);
		}
		return true;
	});

	const categories = [...new Set(options.map(o => o.category))].sort((a, b) => {
		const aLabel = FIELD_ORDER.includes(a) ? a : 'Data Table';
		const bLabel = FIELD_ORDER.includes(b) ? b : 'Data Table';
		return FIELD_ORDER.indexOf(aLabel) - FIELD_ORDER.indexOf(bLabel);
	});

	const treeOptions: TreeSelectItemProps[] = useMemo(() => {
		const ops: TreeSelectItemProps[] = [];
		for (const c of categories) {
			if (c === 'Custom Fields') {
				const sections = [
					...new Set(
						options
							.reduce<string[]>((acc, cur) => {
								const sectionDetails = cur.filterComponentProps as {
									sectionTitle?: string;
									sectionId?: string;
									sectionType?: string;
								};
								// Don't display empty sections
								const hasSubFields = options.some(
									o =>
										o.category === c &&
										(o.filterComponentProps as { sectionTitle?: string; sectionId?: string; sectionType?: string })?.sectionId === sectionDetails?.sectionId
								);
								if (sectionDetails?.sectionTitle && hasSubFields) acc.push(sectionDetails.sectionTitle);
								return acc;
							}, [])
							.sort((a, b) => a.localeCompare(b))
					)
				];
				ops.push({
					label: `${c}`,
					value: c,
					children: sections?.map(sectionTitle => ({
						label: sectionTitle ?? 'Unknown',
						children: [...options]
							.filter(o => o.category === c && (o.filterComponentProps as { sectionTitle?: string })?.sectionTitle === sectionTitle)
							.sort((a, b) => a.label.localeCompare(b.label))
							.map<TreeSelectItemProps>(cfOption => ({
								label: cfOption.label,
								children: [],
								selectable: true,
								value: JSON.stringify({ field: cfOption.field, nested: cfOption.nested, type: cfOption.type })
							})),
						selectable: false
					})),
					selectable: false
				});
			} else {
				ops.push({
					label: `${c}`,
					value: c,
					children: options
						?.filter(o => o.category === c)
						?.map(o => ({
							label: o.label,
							value: JSON.stringify({ field: o.field, nested: o.nested, type: o.type }),
							children: [],
							selectable: true
						})),
					selectable: false
				});
			}
		}

		return ops;
	}, [options, categories]);

	const optionsFlat = useMemo(() => {
		function flattenChildren(nodes: TreeSelectItemProps[], level: number = 0): TreeSelectOption[] {
			return nodes.flatMap(node => {
				const flatNode = {
					props: node,
					nestLevel: level,
					collapsed: true,
					value: node.value
				};

				const children = node.children ? flattenChildren(node.children, level + 1) : [];

				return [flatNode, ...children];
			});
		}

		return flattenChildren(treeOptions);
	}, [treeOptions]);

	const currentTreeOption =
		optionsFlat.find(o => {
			const parsed = tryParseJSONObject<FilterFieldTreeValue>(o.value, {});
			return parsed.field === filterField.field && parsed.nested === filterField.nested && parsed.type === filterField.type;
		}) ?? null;

	function updateValue(value: string | string[]) {
		updateFilter(arg => {
			if (Array.isArray(value)) {
				arg.values = value;
			} else {
				arg.value = value;
			}
		});
	}

	return (
		<ConditionsGrid>
			<TreeSelect
				options={treeOptions}
				size="small"
				fullWidth
				value={currentTreeOption}
				title="Select a field"
				onChange={(_, v) => {
					const value = tryParseJSONObject<FilterFieldTreeValue>(v?.value, {});
					updateFilter(arg => {
						const isSameField = arg.field === value.field && arg.nested === value.nested && arg.type === value.type;
						const newFilterValue = isSameField ? arg.value : null;
						const newFilterValues = isSameField ? arg.values : null;
						let newModifier = arg.modifier;
						if (!isSameField) {
							// Update the filter constraint to automatically select the first one
							const newOption = options.find(f => f.field === value.field && f.nested === value.nested && f.type === value.type);
							const filterOperatorsExt = getExtension<FilterOperatorProps>(`column-operators-${newOption?.type}`);
							newModifier = filterOperatorsExt?.filterConstraints?.[0];
						}

						arg.field = value.field ?? null;
						arg.type = value.type ?? null;
						arg.nested = value.nested;
						arg.value = newFilterValue;
						arg.values = newFilterValues;
						arg.modifier = newModifier;
					});
				}}
				getOptionLabel={o => `${o.props?.label}`}
			/>
			{contraintOptions?.length === 1 ? (
				<div style={{ display: 'grid', placeContent: 'center' }}>
					<Typography textAlign="center">{contraintOptions?.[0]}</Typography>
				</div>
			) : (
				<Select<string, false, true, false>
					options={contraintOptions ?? []}
					size="small"
					fullWidth
					disableClearable={true}
					value={filterField?.modifier ?? null}
					onChange={(_, v) =>
						updateFilter(arg => {
							arg.modifier = v;
						})
					}
					getOptionLabel={o => filterOperatorsExt?.humanNames?.[o] ?? o}
				/>
			)}

			<>
				{filterField?.modifier === 'is empty' ? (
					<></>
				) : FilterComp ? (
					<FilterComp
						filter={filterFieldValue}
						onUpdate={updateValue}
						props={currentOption?.filterComponentProps}
						key={`${currentOption?.category} ${currentOption?.field}`}
					/>
				) : (
					<Extension<FilterExtensionProps>
						name={`column-filter-${currentOption?.type}`}
						key={`${currentOption?.category} ${currentOption?.field}`}
						props={{
							value: getDecodedValue(filterField, currentOption), // currentOption?.decodeValue ? (Array.isArray(filterField.values) ? filterField.values : filterField.value) ?? null,
							setValue: v => {
								updateValue(getEncodedValue(v, currentOption));
							},
							filterParams: currentOption?.filterParams,
							dataSource: currentOption?.dataSource
						}}
					>
						<TextField size="small" fullWidth />
					</Extension>
				)}
			</>
		</ConditionsGrid>
	);
}

type FilterGroupProps = {
	items: CriteriaGroup['items'];
	updateItems: (draft: DraftFunction<CriteriaGroup>) => void;
	options: FilterExtension[];
	filtersLogic: CriteriaGroup['filtersOp'];
	filterType?: string;
};

export function FilterGroup({ items, updateItems, options, filtersLogic, filterType }: FilterGroupProps) {
	return (
		<FlexColumnSpaced>
			<FlexColumnSpaced>
				{items.map((filterField, index) => {
					return (
						<FlexRow style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }} key={`${filterField.field}-${index}`}>
							<div style={{ padding: '0.5rem', width: '7rem' }}>{index === 0 ? <Typography>Where</Typography> : <Typography>{filtersLogic}</Typography>}</div>
							<FilterField
								key={index}
								options={options}
								filterField={filterField}
								updateFilter={setter => {
									updateItems(arg => setter(arg.items[index]));
								}}
								filterType={filterType}
							/>
							{items.length > 1 ? <IconButton iconName="Delete" onClick={() => updateItems(arg => arg.items.splice(index, 1))} /> : null}
						</FlexRow>
					);
				})}
			</FlexColumnSpaced>
			<Button color="primary" onClick={() => updateItems(arg => arg.items.push(EMPTY_FILTER))} style={{ marginRight: 'auto' }}>
				Add Criteria
			</Button>
		</FlexColumnSpaced>
	);
}

export const FilterList = ({
	initialFilters,
	onChange,
	filterExtensionSource,
	additionalFilters,
	filterType
}: {
	initialFilters?: GroupRoot;
	onChange?: (args: GroupRoot) => void;
	filterExtensionSource: string;
	additionalFilters?: FilterExtension[];
	filterType?: string;
}) => {
	const [filters, setFilters] = useImmerCall<GroupRoot>(initialFilters ?? EMPTY_ROOT, onChange);
	return (
		<>
			<FlexColumnSpaced>
				{filters.items.map((fg, idx) => (
					<FilterComp
						key={idx} // can't really use idx alone here or fun expand stuff happens with accordions and we have no identifier 😞
						fg={fg}
						idx={idx}
						filters={filters}
						setFilters={setFilters}
						filterExtensionSource={filterExtensionSource}
						additionalFilters={additionalFilters}
						filterType={filterType}
					/>
				))}
				<AddGroup
					updateItems={arg => {
						setFilters(draft => {
							arg(draft);
						});
					}}
				/>
			</FlexColumnSpaced>
		</>
	);
};

const FilterComp = ({
	idx,
	filters,
	fg,
	setFilters,
	filterExtensionSource,
	additionalFilters,
	filterType
}: {
	idx: number;
	filters: GroupRoot;
	fg: CriteriaGroup;
	setFilters: Updater<GroupRoot>;
	filterExtensionSource: string;
	additionalFilters?: FilterExtension[];
	filterType?: string;
}) => {
	const [expand, setExpand] = useState(true);
	const theme = useContext(ThemeContext);
	const filtersLength = filters?.items?.length;
	const extFilterOptions = getExtensions<FilterExtension>(filterExtensionSource);
	const filterOptions = [...extFilterOptions, ...(additionalFilters ?? [])];
	const isNotLast = idx !== filters.items.length - 1;
	
	const buildActionMenu: () => ActionsMenuOption[] = () => {
		const deleteFunction = async () => {
			setFilters(draft => {
				draft.items.splice(idx, 1);
			});
		};

		const deleteOption = deleteAction(deleteFunction, `Rule ${idx + 1}`);
		const actions: ActionsMenuOption[] = [deleteOption];

		return actions;
	};

	return (
		<React.Fragment>
			<Card>
				<CardHeader
					title={`Rule ${idx + 1}`}
					subheader={<div>In this search, include results that meet the following criteria:</div>}
					action={
						<FlexRowSpacedCentered>
							<GroupLogic
								value={ChainLogic[fg.filtersOp.toUpperCase()]}
								kind="within"
								onChange={v =>
									setFilters(draft => {
										draft.items[idx].filtersOp = v as CriteriaGroup['filtersOp'];
									})
								}
							/>
							<ActionMenuPopover options={buildActionMenu()} />
							<ExpandMore expand={expand} iconName="ExpandMore" onClick={() => setExpand(!expand)} aria-expanded={expand} aria-label="show more" />
						</FlexRowSpacedCentered>
					}
					sx={{ backgroundColor: theme?.colors?.grey?.[100] }}
				/>
				<Collapse in={expand} timeout="auto" unmountOnExit>
					<CardContent style={{ padding: '2rem' }}>
						<FilterGroup
							items={fg.items}
							filtersLogic={fg.filtersOp}
							options={filterOptions}
							updateItems={arg => {
								setFilters(draft => {
									arg(draft.items[idx]);
								});
							}}
							filterType={filterType}
						/>
					</CardContent>
				</Collapse>
			</Card>
			{filtersLength > 1 && isNotLast ? (
				<GroupLogic
					value={ChainLogic[filters.groupOp.toUpperCase()]}
					kind="between"
					onChange={v =>
						setFilters(draft => {
							draft.groupOp = v as GroupRoot['groupOp'];
						})
					}
				/>
			) : (
				<></>
			)}
		</React.Fragment>
	);
};

export const AddGroup = ({ updateItems }: { updateItems: (draft: DraftFunction<GroupRoot>) => void }) => {
	return (
		<Button
			variant="secondary"
			color="secondary"
			style={{ marginRight: 'auto' }}
			onClick={() => {
				updateItems(arg => {
					// Need to push a mutable copy into immer draft
					arg.items.push({ ...EMPTY_GROUP, items: [EMPTY_FILTER] });
				});
			}}
		>
			Add New Rule
		</Button>
	);
};

const GroupLogic = ({ value, onChange }: { value: unknown; kind: 'between' | 'within'; onChange: (v: string) => void }) => {
	return (
		<FlexRowSpacedCentered>
			<Typography variant="body1">Define how the criteria work together</Typography>
			<MuiRadioGroup
				row
				value={value ?? undefined}
				style={{ padding: '0.5rem 2rem', gap: '1rem' }}
				onChange={(_, v) => {
					if (v) {
						onChange(v);
					}
				}}
			>
				<FormControlLabel value={ChainLogic.AND} control={<MuiRadio />} label={<FormLabel>And</FormLabel>} />
				<FormControlLabel value={ChainLogic.OR} control={<MuiRadio />} label={<FormLabel>Or</FormLabel>} />
			</MuiRadioGroup>
		</FlexRowSpacedCentered>
	);
};
